/**
 * Google OAuth Authentication
 * KISS implementation - no external libraries
 */

import { writable } from 'svelte/store';
import { permissionsApi } from './permissions/PermissionsService';
import { scheduleTokenRefresh, stopTokenRefresh, msUntilExpiry } from './tokenRefresh';

// Runtime configuration from window.__APP_CONFIG__ (injected at container startup)
// Falls back to import.meta.env for local development
const GOOGLE_CLIENT_ID =
  window.__APP_CONFIG__?.googleClientId || import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
// Resolved on use, not at import: api/documents.ts imports this module, and node-env unit tests
// have no window.location.
const redirectUri = (): string =>
  window.__APP_CONFIG__?.oauthRedirectUri ||
  import.meta.env.VITE_OAUTH_REDIRECT_URI ||
  `${window.location.origin}/auth/callback`;

const ADMIN_EMAILS = ['user@example.com', 'admin@example.com']; // Replace with actual admin emails

export interface AuthUser {
  email: string;
  name: string;
  picture: string;
  token: string;
  isAdmin: boolean;
}

// Auth store
function createAuthStore() {
  // Skip initialization inside silent-refresh iframe — the iframe only relays
  // the token via postMessage and must not make API calls or clear shared localStorage.
  const isIframe = typeof window !== 'undefined' && window.self !== window.top;

  let initialUser: AuthUser | null = null;

  if (!isIframe) {
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');

    initialUser = storedToken && storedUser ? JSON.parse(storedUser) : null;
    if (initialUser) {
      // Check if stored token is already expired
      if (storedToken && msUntilExpiry(storedToken) === 0) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        initialUser = null;
      } else {
        initialUser.isAdmin = ADMIN_EMAILS.includes(initialUser.email);
        // Fetch permissions if an existing session is found
        permissionsApi.fetchPermissions();
        // Schedule automatic token refresh
        scheduleTokenRefresh();
      }
    }
  }

  const { subscribe, set } = writable<AuthUser | null>(initialUser);

  return {
    subscribe,
    login: (user: Omit<AuthUser, 'isAdmin'>) => {
      const authUser: AuthUser = {
        ...user,
        isAdmin: ADMIN_EMAILS.includes(user.email),
      };
      localStorage.setItem('auth_token', authUser.token);
      localStorage.setItem('auth_user', JSON.stringify(authUser));
      set(authUser);
      // Fetch permissions after successful login
      permissionsApi.fetchPermissions();
      // Schedule automatic token refresh
      scheduleTokenRefresh();
    },
    logout: () => {
      stopTokenRefresh();
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('user_locale');
      set(null);
    },
    getToken: (): string | null => {
      return localStorage.getItem('auth_token');
    },
  };
}

export const authStore = createAuthStore();

// Post-login redirect helpers
export const POST_LOGIN_REDIRECT_KEY = 'plc_post_login_redirect';

export function getAndClearPostLoginRedirect(): string {
  const redirect = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY) || '/';
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  return redirect;
}

/**
 * Start Google OAuth flow
 * Using implicit flow and requesting token in URL fragment.
 * Google will redirect to our /auth/callback route where we'll parse it.
 */
export function startGoogleLogin() {
  // Save current path so we can redirect back after login
  const currentPath = window.location.pathname;
  if (
    currentPath &&
    currentPath !== '/' &&
    currentPath !== '/login' &&
    !currentPath.startsWith('/auth/callback')
  ) {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, currentPath);
  }

  // Generate and store state for CSRF protection
  const state = generateRandomString();
  sessionStorage.setItem('oauth_state', state);

  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: redirectUri(),
    response_type: 'token id_token',
    scope: 'openid email profile',
    nonce: generateRandomString(),
    state: state,
  });

  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
}

/**
 * Handle OAuth callback by parsing the token from the URL fragment.
 */
export function handleOAuthCallback(): Omit<AuthUser, 'isAdmin'> | null {
  const hash = window.location.hash.substring(1);
  if (!hash) {
    console.error('No hash fragment found in callback URL');
    return null;
  }

  const params = new URLSearchParams(hash);
  const idToken = params.get('id_token');
  const state = params.get('state');

  // Validate state
  const expectedState = sessionStorage.getItem('oauth_state');
  if (state !== expectedState) {
    console.error('State mismatch - possible CSRF attack');
    return null;
  }
  sessionStorage.removeItem('oauth_state');

  if (!idToken) {
    console.error('No id_token in URL fragment');
    return null;
  }

  try {
    // Decode JWT to get user info
    const payload = JSON.parse(atob(idToken.split('.')[1]));

    return {
      email: payload.email,
      name: payload.name,
      picture: payload.picture,
      token: idToken,
    };
  } catch (e) {
    console.error('Failed to process id_token:', e);
    return null;
  }
}

/**
 * Generate random string for state/nonce
 */
function generateRandomString(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}
